<script lang="ts">
  import GripVertical from '@lucide/svelte/icons/grip-vertical';
  import MessageSquare from '@lucide/svelte/icons/message-square';
  import MessageSquareText from '@lucide/svelte/icons/message-square-text';
  import Trash2 from '@lucide/svelte/icons/trash-2';

  import { getServices } from '../../app/context';
  import type { Range } from '../../model/filter';
  import { moveKink, removeKink, renameKink, setRating } from '../../model/ops';
  import type { Category, Id, Kink } from '../../model/types';
  import { format, reorderMessages, strings } from '../../strings';
  import Highlight from '../../ui/Highlight.svelte';
  import IconButton from '../../ui/IconButton.svelte';
  import InlineEdit from '../../ui/InlineEdit.svelte';
  import RatingPicker from '../../ui/RatingPicker.svelte';
  import { reorderHandle, type ReorderState } from '../../ui/reorder.svelte';

  interface Props {
    category: Category;
    kink: Kink;
    matches?: readonly Range[];
    /** Shown but de-emphasized by the quick filter (e.g. fully rated under "Unrated"). */
    dimmed?: boolean;
    /** Set when the kink can be reordered (edit mode, unfiltered list). */
    reorder?: ReorderState | null;
    oncomment: (kink: Kink, trigger: HTMLElement) => void;
  }

  let { category, kink, matches = [], dimmed = false, reorder = null, oncomment }: Props = $props();

  const { store, view, toasts, settings } = getServices();
  const multi = $derived(category.roles.length > 1);
  const unsetId = $derived(store.doc.ratings[0]?.id ?? '');
  const preview = $derived(
    kink.comment && kink.comment.length > 120 ? `${kink.comment.slice(0, 117)}…` : kink.comment,
  );

  const handle = $derived(
    reorder &&
      reorderHandle(reorder, {
        group: `kinks:${category.id}`,
        id: kink.id,
        label: kink.name,
        index: () => category.kinks.findIndex((k) => k.id === kink.id),
        count: () => category.kinks.length,
        move: (to) => store.apply((doc) => moveKink(doc, kink.id, to)),
        begin: () => {
          store.beginGesture();
        },
        end: (commit) => {
          store.endGesture(commit);
        },
        announce: (message) => {
          view.announce(message);
        },
        messages: reorderMessages,
      }),
  );

  function remove() {
    store.apply((doc) => removeKink(doc, kink.id));
    toasts.show(format.deleted(kink.name), {
      action: { label: strings.undo, run: () => store.undo() },
    });
  }
</script>

<li
  data-reorder-group="kinks:{category.id}"
  data-reorder-id={kink.id}
  class={[
    'flex flex-col gap-1.5 border-t border-line py-2 first:border-t-0',
    reorder?.active === kink.id && 'relative z-10 rounded-lg bg-surface-2 ring-2 ring-focus',
  ]}
>
  <div class="flex items-start gap-1">
    {#if view.editMode}
      {#if handle}
        <button
          type="button"
          aria-label={format.reorder(kink.name)}
          class="grid size-8 shrink-0 cursor-grab place-items-center rounded-md text-ink-muted hover:bg-surface-2 hover:text-ink aria-pressed:cursor-grabbing aria-pressed:text-focus pointer-coarse:size-11"
          {...handle}
        >
          <GripVertical size={16} aria-hidden="true" />
        </button>
      {/if}
      <div class="min-w-0 flex-1 pt-0.5 text-sm font-medium">
        <InlineEdit
          value={kink.name}
          label={format.rename(kink.name)}
          onsubmit={(name: string) => store.apply((doc) => renameKink(doc, kink.id, name))}
        />
      </div>
      <IconButton
        size="sm"
        tooltipAlign="end"
        icon={Trash2}
        label={format.remove(kink.name)}
        class="hover:text-danger"
        onclick={remove}
      />
    {:else}
      <span class="min-w-0 flex-1 pt-1 text-sm font-medium break-words">
        <Highlight text={kink.name} ranges={matches} />
      </span>
    {/if}
    <IconButton
      size="sm"
      tooltipAlign="end"
      icon={kink.comment ? MessageSquareText : MessageSquare}
      active={kink.comment !== undefined}
      class="print:hidden"
      label={kink.comment ? format.editComment(kink.name) : format.addComment(kink.name)}
      tooltipText={preview}
      description={kink.comment}
      onclick={(event: MouseEvent) => {
        if (event.currentTarget instanceof HTMLElement) oncomment(kink, event.currentTarget);
      }}
    />
  </div>
  <div class="flex flex-wrap gap-x-4 gap-y-1">
    {#each category.roles as role (role.id)}
      {@const rated = (kink.ratings[role.id] ?? unsetId) !== unsetId}
      <div class="flex flex-col gap-1">
        {#if multi}
          <span class="px-1 text-xs leading-4 text-ink-muted" aria-hidden="true">{role.name}</span>
        {/if}
        <!-- Dim (never hide) what the quick filter no longer needs; text keeps full contrast. -->
        <div
          class={[
            'transition-opacity duration-150',
            (dimmed || (rated && settings.value.filter === 'unrated')) &&
              'dimmed opacity-40 focus-within:opacity-100 hover:opacity-100',
          ]}
        >
          <RatingPicker
            ratings={store.doc.ratings}
            value={kink.ratings[role.id] ?? unsetId}
            label={format.pickerLabel(kink.name, multi ? role.name : undefined)}
            roleName={multi ? role.name : undefined}
            onchange={(ratingId: Id) => {
              store.apply((doc) => setRating(doc, kink.id, role.id, ratingId), {
                coalesce: `rate:${kink.id}:${role.id}`,
              });
            }}
          />
        </div>
      </div>
    {/each}
  </div>
</li>
